import { Box, Container, Grid, Link, Skeleton, Typography } from "@mui/material";
import ContentHeader from "../../components/layout/ContentHeader/ContentHeader";
import ContentTitle from "../../components/layout/ContentTitle/ContentTitle";
import Section from "../../components/layout/Section/Section";
import { useEffect, useState } from "react";
import { apiClient } from "../../services/api_client";
import NavBreadcrumbs from "../../components/navigation/NavBreadcrumbs/NavBreadcrumbs";
import { PAGE } from "../../constants/page";
import Flow from "../../components/display/Flow/Flow";
import Hr from "../../components/ui/Hr/Hr";
import GridTable from "../../components/display/GridTable/GridTable";
import parse from 'html-react-parser';
import OtherSection from "../../components/display/OtherSection/OtherSection";
import { scrollIntoView } from "../../utils/scrollIntoView";

interface Props {
  children?: React.ReactElement<unknown>;
}
const texts = [
  "サプライムは様々な業界や企業の課題を",
  "一緒に解決できる仲間を募集しています。",
  "私たちと一緒に未来の社会に貢献しませんか？"
]

const flows: Array<Flow> = [
  {
    step: "1",
    title: "エントリー",
    contents: [
      "お問い合わせフォームからご応募ください。"
    ]
  },
  {
    step: "2",
    title: "書類選考",
    contents: [
      "履歴書・職務経歴書などの書類をもとに、選考を行います。"
    ]
  },
  {
    step: "3",
    title: "面接",
    contents: [
      "担当者との面接を実施します。",
      "これまでのご経験や志望動機についてお聞かせください。",
    ]
  },
  {
    step: "4",
    title: "内定",
    contents: [
      "面接結果を踏まえ、合否のご連絡を差し上げます。",
      "内定後、条件面談・入社日の調整を行います。",
    ]
  }
]

export default function Recruit(props: Props) {
  /** 読み込み中判定 */
  const [isLoading, setIsLoading] = useState<boolean>(true)
  /** 新卒採用情報 */
  const [grad, setGrad] = useState<Recruit>({
    summary: '',
    eligibility: '',
    jobDescription: '',
    selectionProcess: '',
    plannedHiresThisYear: '',
    actualHiresLastYear: '',
    workLocation: '',
    workHours: '',
    holidaysAndLeave: '',
    salaries: '',
    trainingPrograms: '',
    benefits: ''
  })
  /** 経験者採用情報 */
  const [career, setCareer] = useState<Recruit>({
    summary: '',
    eligibility: '',
    jobDescription: '',
    selectionProcess: '',
    plannedHiresThisYear: '',
    actualHiresLastYear: '',
    workLocation: '',
    workHours: '',
    holidaysAndLeave: '',
    salaries: '',
    trainingPrograms: '',
    benefits: ''
  })

  /** 新卒採用カラム */
  const [gradColumns, setGradColumns] = useState<Array<GridTableColumn<Recruit>>>([
    {
      field: 'eligibility',
      label: '応募資格',
    },
    {
      field: 'jobDescription',
      label: '業務内容',
    },
    {
      field: 'selectionProcess',
      label: '選考方法と重視点',
    },
    {
      field: 'plannedHiresThisYear',
      label: '今年度採用予定数',
    },
    {
      field: 'actualHiresLastYear',
      label: '前年度採用実績数',
    },
    {
      field: 'workLocation',
      label: '勤務地',
    },
    {
      field: 'workHours',
      label: '勤務時間',
    },
    {
      field: 'holidaysAndLeave',
      label: '休日休暇',
    },
    {
      field: 'salaries',
      label: '給与',
    },
    {
      field: 'trainingPrograms',
      label: '教育研修',
    },
    {
      field: 'benefits',
      label: '福利厚生',
    }
  ])

  /** 経験者採用カラム */
  const [careerColumns, setCareerColumns] = useState<Array<GridTableColumn<Recruit>>>([
    {
      field: 'jobDescription',
      label: '業務内容',
    },
    {
      field: 'workHours',
      label: '勤務時間',
    },
    {
      field: 'holidaysAndLeave',
      label: '休日休暇',
    },
    {
      field: 'benefits',
      label: '福利厚生',
    }
  ])

  useEffect(() => {
    // 採用情報取得API
    const fetchData = async () => {
      const getInformation = await apiClient.getInformation({
        // mock: 'GetInformation.json'
      }).finally(() => {
        setIsLoading(false)
      })
      setGrad(getInformation.grad)
      setCareer(getInformation.career)
    }
    fetchData()
  }, [])

  useEffect(() => {
    scrollIntoView()
  }, [grad, career])

  return (
    <>
      <Section color="dark">
        <ContentHeader title={PAGE.RECRUIT.NAME} subTitle={PAGE.RECRUIT.TEXT} />
      </Section>

      <Section color='dark' height="50vh" backgroundImage='url(/assets/images/recruit.jpg)'>
        <Container>
          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px', marginBottom: '100px' }}>
            {texts.map((t, index) => {
              return (
                <Typography
                  key={index}
                  variant="body1"
                  component="p"
                  sx={(theme) => ({
                    fontSize: '1.1rem',
                    [theme.breakpoints.up('md')]: {
                      fontSize: '1.25rem',
                    },
                    [theme.breakpoints.up('lg')]: {
                      fontSize: '1.5rem',
                    },
                  })}
                >
                  {t}
                </Typography>
              )
            })}
          </Grid>
        </Container>
      </Section>
      <Hr />

      <Section color="light" padding="1rem 0">
        <Container>
          <NavBreadcrumbs />
        </Container>
      </Section>

      <Section color="light">
        <Container id="newgrads">
          <ContentTitle title={'NEW GRADS'} subTitle={'新卒採用情報'} />
          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              募集要項
            </Typography>
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '50px' }}>
            <Typography
              variant="body1"
              component="div"
              color="text.primary"
            >
              {isLoading ?
                <Skeleton animation="wave" width={'100%'} />
                :
                parse(grad.summary || '')
              }
            </Typography>
          </Grid>
          <GridTable color="light" columns={gradColumns} data={grad} isLoading={isLoading} />

          <Hr color={"light"} />

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '3rem', marginBottom: '3rem' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              エントリー方法
            </Typography>
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '3rem', marginBottom: '3rem' }}>
            <Typography variant="body1">
              こちらの
              <Link href={PAGE.CONTACT.PATH} fontWeight={'bold'}>お問い合わせフォーム</Link>
              からエントリーできます。
            </Typography>
          </Grid>
        </Container>
      </Section>

      <Section color="dark">
        <Container id="experienced" >
          <ContentTitle title={'EXPERIENCED'} subTitle={'経験者採用情報'} />
          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              募集要項
            </Typography>
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '50px' }}>
            <Typography
              variant="body1"
              component="div"
              color="text.primary"
            >
              {isLoading ?
                <Skeleton animation="wave" width={'100%'} />
                :
                parse(career.summary || '')
              }
            </Typography>
          </Grid>
          <GridTable color="dark" columns={careerColumns} data={career} isLoading={isLoading} />

          <Hr color={"dark"} />

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '3rem', marginBottom: '3rem' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              エントリー方法
            </Typography>
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '3rem', marginBottom: '3rem' }}>
            <Typography variant="body1">
              こちらの
              <Link href={PAGE.CONTACT.PATH} fontWeight={'bold'}>お問い合わせフォーム</Link>
              からエントリーできます。
            </Typography>
          </Grid>
        </Container>
      </Section>

      <Section color="light">
        <Container id="steps" >
          <ContentTitle title={'STEPS'} subTitle={'採用フロー'} />
          <Box margin={'5rem 1rem'} >
            <Flow flows={flows} />
          </Box>
        </Container>
      </Section>

      <Hr />

      <Section color='dark' height={'300px'} backgroundImage='url(/assets/images/top.jpg)' isBackgroundCover>
        <Container>
          <OtherSection isObserver={false} />
        </Container>
      </Section>
    </>
  )
}